import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { toast } from "react-toastify";

import { webhookCardapioService } from "../../../../services/webhookCardapioService";
import type {
  WebhookCardapioEvent,
  WebhookCardapioEventosResponse,
} from "../../../../services/webhookCardapioService";
import type { TableAction } from "../../../../utils/tableActions";
import {
  formatWebhookEventoDate,
  resumoWebhookPayload,
} from "../utils/eventosTable";

interface WebhookCardapioContextType {
  page: number;
  setPage: (page: number) => void;
  payloadModal: WebhookCardapioEvent | null;
  setPayloadModal: (ev: WebhookCardapioEvent | null) => void;
  closePayloadModal: () => void;
  webhookUrl: string;
  copyToClipboard: () => void;
  eventosData: WebhookCardapioEventosResponse | undefined;
  isEventosLoading: boolean;
  tableData: string[][];
  tableActions: TableAction[];
}

const WebhookCardapioContext = createContext<
  WebhookCardapioContextType | undefined
>(undefined);

export const WebhookCardapioProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [page, setPage] = useState(1);
  const [payloadModal, setPayloadModal] = useState<WebhookCardapioEvent | null>(
    null
  );

  const webhookUrl = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || "http://localhost:3001";
    return `${base.replace(/\/$/, "")}/webhooks/cardapio`;
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["webhook-cardapio-eventos", page],
    queryFn: () => webhookCardapioService.getEventos(page, 20),
  });

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("URL copiada para a área de transferência.");
  }, [webhookUrl]);

  const closePayloadModal = useCallback(() => {
    setPayloadModal(null);
  }, []);

  const tableData = useMemo(
    () =>
      (data?.data ?? []).map((ev) => [
        formatWebhookEventoDate(ev.createdAt),
        ev.eventType ?? "—",
        resumoWebhookPayload(ev.payload),
      ]),
    [data?.data]
  );

  const tableActions: TableAction[] = useMemo(
    () => [
      {
        icon: <Eye size={16} />,
        onClick: (rowIndex) => {
          const ev = data?.data?.[rowIndex];
          if (ev) setPayloadModal(ev);
        },
        className: "text-primary hover:text-primary-dark",
      },
    ],
    [data?.data]
  );

  return (
    <WebhookCardapioContext.Provider
      value={{
        page,
        setPage,
        payloadModal,
        setPayloadModal,
        closePayloadModal,
        webhookUrl,
        copyToClipboard,
        eventosData: data,
        isEventosLoading: isLoading,
        tableData,
        tableActions,
      }}
    >
      {children}
    </WebhookCardapioContext.Provider>
  );
};

export const useWebhookCardapioPage = () => {
  const ctx = useContext(WebhookCardapioContext);
  if (!ctx) {
    throw new Error(
      "useWebhookCardapioPage must be used within WebhookCardapioProvider"
    );
  }
  return ctx;
};
